import { inferReadonlyArray } from "@aprao/common/src/array/inferReadonlyArray.js";
import { pull } from "@aprao/common/src/array/pull.js";
import {
  type BigFraction,
  bigFractionFromFraction,
} from "@aprao/common/src/math/BigFraction.js";
import { divide } from "@aprao/common/src/math/divide.js";
import { FRACTION_ZERO } from "@aprao/common/src/math/fraction.js";
import { opposite } from "@aprao/common/src/math/opposite.js";
import { sum, sumKey } from "@aprao/common/src/math/sum.js";
import type {
  CountableNumber,
  FundingType,
  PositiveInteger,
  WithRelativesSet,
} from "@aprao/common/src/shapes/common.js";
import { times } from "@aprao/common/src/util/times.js";
import { makeAutoObservable, toJS } from "mobx";
import { computedFn } from "mobx-utils";
import { mapForNullable } from "option-t/nullable";

import type {
  ResidentialAreaKeys,
  ResidentialCostKeys,
  ResidentialDepositKeys,
  ResidentialPriceKeys,
  ShapeResidentialSalesGroup,
  ShapeResidentialSalesItem,
} from "../../shapes/ShapeRevenueGroup.js";
import type { WithRelativesSetDelegate } from "../delegates/WithRelativesSetDelegate.js";
import {
  type NamedRelativesSetFacade,
  namedRelativesSetFacadeWithAbsolute,
} from "../NamedRelativesSetFacade.js";
import {
  type RelativesSetFacade,
  relativesSetFacadeWithAbsolute,
} from "../RelativesSetFacade.js";
import { ResidentialSalesItemFacade } from "./ResidentialSalesItemFacade.js";
import { RevenueGroupBaseFacade } from "./RevenueGroupBaseFacade.js";
import { SalesGroupBaseFacade } from "./SalesGroupBaseFacade.js";
import { SalesProjectionPeriodsDelegate } from "./SalesProjectionPeriodsDelegate.js";

export type ResidentialCostFacade =
  NamedRelativesSetFacade<ResidentialCostKeys>;

// The deposit absolute value is the mean deposit value
// => total deposit / count of units
type DepositFacade = RelativesSetFacade<
  ResidentialDepositKeys,
  WithRelativesSetDelegate<ResidentialDepositKeys>
>;

export class ResidentialSalesGroupFacade {
  readonly category = "residential";

  readonly $model;

  readonly _revenueGroupBase;

  readonly _salesGroupBase;

  constructor(model: ShapeResidentialSalesGroup) {
    makeAutoObservable(this);

    this.$model = model;
    this._revenueGroupBase = new RevenueGroupBaseFacade(this.$model);
    this._salesGroupBase = new SalesGroupBaseFacade(this.$model, {
      areaTotal: () => this.areaTotal,
      valueGross: () => this.valueGross,
    });
  }

  readonly _decorateItem = computedFn(
    (item: ShapeResidentialSalesItem) => new ResidentialSalesItemFacade(item),
  );

  get items() {
    return inferReadonlyArray(
      this.$model.items.map((item) => this._decorateItem(item)),
    );
  }

  get totalCount() {
    return this.items
      .map(({ quantity }) => quantity)
      .reduce((prev, curr) => prev + curr, 0) as PositiveInteger;
  }

  get valueGross() {
    return mapForNullable(sumKey(this.items, "aggregateValue"), (value) =>
      value.mul(this.valueGrossScale),
    );
  }

  get areaTotal() {
    return sumKey(this.items, "aggregateArea");
  }

  get meanValue() {
    return this.valueGross?.div(this.totalCount) ?? null;
  }

  addItem(params: {
    quantity: CountableNumber;
    description: string;
    area: WithRelativesSet<ResidentialAreaKeys>;
    salePrice: WithRelativesSet<ResidentialPriceKeys>;
  }) {
    this.$model.items.push(
      toJS({
        quantity: params.quantity,
        description: params.description,
        area: params.area,
        salePrice: params.salePrice,
      }),
    );
  }

  removeItem(item: ResidentialSalesItemFacade) {
    pull(this.$model.items, item.$model);
  }

  breakdownItem(item: ResidentialSalesItemFacade) {
    if (item.quantity < 2) {
      return;
    }

    this.removeItem(item);
    times(item.quantity, () => {
      this.addItem(
        toJS({
          quantity: 1 as CountableNumber,
          description: item.description,
          area: {
            relatives: {
              ...item.$model.area.relatives,
              // When breaking down, if an absolute (total) value is defined, we
              // need to divide by the number of items.
              absolute: mapForNullable(
                item.$model.area.relatives.absolute,
                (value) =>
                  bigFractionFromFraction(value)
                    .div(item.quantity)
                    .toFraction(),
              ),
            },
          },
          salePrice: {
            relatives: {
              ...item.$model.salePrice.relatives,
              absolute: mapForNullable(
                item.$model.salePrice.relatives.absolute,
                (value) =>
                  bigFractionFromFraction(value)
                    .div(item.quantity)
                    .toFraction(),
              ),
            },
          },
        }),
      );
    });
  }

  get agentFee() {
    return relativesSetFacadeWithAbsolute(this.$model.agentFee, {
      ratioValue: () => this.valueGross,
    });
  }

  get legalFee() {
    return relativesSetFacadeWithAbsolute(this.$model.legalFee, {
      ratioValue: () => this.valueGross,
    });
  }

  readonly _decorateCost = computedFn((cost: ResidentialCostFacade["$model"]) =>
    namedRelativesSetFacadeWithAbsolute(cost, {
      ratioValue: () => this.valueGross,
    }),
  );

  get costs() {
    return inferReadonlyArray(
      this.$model.costs.map((cost) => this._decorateCost(cost)),
    );
  }

  addCost() {
    this.$model.costs.push({
      name: `Custom cost ${this.$model.costs.length + 1}`,
      relatives: {
        absolute: null,
        ratioValue: null,
      },
    });
  }

  removeCost(cost: ResidentialCostFacade) {
    pull(this.$model.costs, cost.$model);
  }

  get costsTotal() {
    return sum([
      this.agentFee.relativesSet.getRelative("absolute"),
      this.legalFee.relativesSet.getRelative("absolute"),
      ...this.costs.map((cost) => cost.relativesSet.getRelative("absolute")),
    ]);
  }

  get costsMean() {
    return mapForNullable(this.costsTotal, (cost) =>
      0 < this.totalCount ? cost.div(this.totalCount) : FRACTION_ZERO,
    );
  }

  /**
   * The ratio of costs and fees over the group total value.
   */
  get costAndFeesOnValue() {
    return divide(this.costsTotal, this.valueGross);
  }

  get deposit(): DepositFacade {
    return relativesSetFacadeWithAbsolute(this.$model.deposit, {
      ratioValueMean: () => this.meanValue,
    });
  }

  get depositProjectionPeriods() {
    return new SalesProjectionPeriodsDelegate({
      periods: () => this.$model.depositProjectionPeriods,
      totalCount: () => this.totalCount,
    });
  }

  get balanceProjectionPeriods() {
    return new SalesProjectionPeriodsDelegate({
      periods: () => this.$model.balanceProjectionPeriods,
      totalCount: () => this.totalCount,
    });
  }

  get depositMean() {
    // deposit absolute value is already a "mean" value, so we simply return it
    return this.deposit.relativesSet.getRelative("absolute");
  }

  get balanceMean() {
    return sum([
      this.meanValue,
      opposite(this.deposit.relativesSet.getRelative("absolute")),
    ]);
  }

  get projectionDuration() {
    return Math.max(
      // Since we hide the deposit row when there is no deposit we also need
      // to exclude it from duration calculation. In case it was spread while
      // a value was set and then nullified it would extend the cash flow
      // without any way to clear the row.
      this.depositMean == null ? 0 : this.depositProjectionPeriods.duration,
      this.balanceProjectionPeriods.duration,
    );
  }

  getGrossProceedsProjectionAtPeriodIndex = (periodIndex: number) => {
    const depositMean = this.depositMean ?? FRACTION_ZERO;
    const valueMean = this.balanceMean ?? FRACTION_ZERO;

    const periodDeposit = depositMean.mul(
      this.depositProjectionPeriods.getCountAtPeriodIndex(periodIndex) ?? 0,
    );
    const periodBalance = valueMean.mul(
      this.balanceProjectionPeriods.getCountAtPeriodIndex(periodIndex) ?? 0,
    );

    return periodDeposit.add(periodBalance);
  };

  getSalesCostProjectionAtPeriodIndex = (periodIndex: number) => {
    const costsMean = this.costsMean ?? FRACTION_ZERO;
    return costsMean.mul(
      this.balanceProjectionPeriods.getCountAtPeriodIndex(periodIndex) ?? 0,
    );
  };

  // below are all the delegates
  get name() {
    return this._revenueGroupBase.name;
  }

  get fundingType() {
    return this._revenueGroupBase.fundingType;
  }

  get valueGrossScale() {
    return this._revenueGroupBase.valueGrossScale;
  }

  set name(name: string) {
    this._revenueGroupBase.name = name;
  }

  set fundingType(fundingType: FundingType) {
    this._revenueGroupBase.fundingType = fundingType;
  }

  set valueGrossScale(ratio: BigFraction) {
    this._revenueGroupBase.valueGrossScale = ratio;
  }

  get linkedBuildPhaseRefId() {
    return this._salesGroupBase.linkedBuildPhaseRefId;
  }

  get averageValuePerArea() {
    return this._salesGroupBase.averageValuePerArea;
  }
}
